import { Alert, Badge, Button, Card, Label, Spinner } from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import axios from "../../config/axiosInstance";
import { QUERIES } from "../../config/apiConfig";
import { HiInformationCircle } from "react-icons/hi";
import HeaderQuery from "./HeaderQuery";
import usePDF from "../../hooks/usePDF";
import TitleStripe from "../common/TitleStripe";
import CardQueryDescription from "../common/CardQueryDescription";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import FooterQuery from "./FooterQuery";
import { FaPrint } from "react-icons/fa";
import { useReactToPrint } from "react-to-print";
import QueryVehicleInput from "../inputs/QueryVehicleInput";
import moment from "moment";

const BaseNacionalQuery = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [document, setDocument] = useState("");
  const [isDoneRequest, setIsDoneRequest] = useState(false);
  const [error, setError] = useState("");
  const [errorQuery, setErrorQuery] = useState("");
  const [data, setData] = useState(null);
  const componentRef = useRef();
  const printRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef: printRef });
  const [selectedQueries, setSelectedQueries] = useState([]);
  const { handleClickDownloadPDF, pdfURL, setPdfURL } = usePDF(
    componentRef,
    info,
    query?.id,
    isDoneRequest
  );

  useEffect(() => {
    if (dataFromLocalStorage) {
      setData(dataFromLocalStorage);
      setDocument(dataFromLocalStorage?.dadosCadastrais?.cpf);
      setIsDoneRequest(true);
    }
  }, []);

  const fetchInfo = async (raw) => {
    return await axios(`${QUERIES.GET_V2_QUERY}/base-nacional/${info}`);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDocument(info);

    let isValid = true;

    if (isValid) {
      try {
        setPdfURL("");
        setData(null);
        setIsLoading(true);
        setError("");
        setErrorQuery("");
        setIsDoneRequest(false);
        // Fetch principal
        const { data: dados } = await fetchInfo();
        setData(dados.data);
        setIsLoading(false);
      } catch (error) {
        setErrorQuery(
          typeof error?.response?.data?.message !== "object"
            ? error?.response?.data?.message
            : "Algum erro ocorreu com sua consulta"
        );
        setIsLoading(false);
      } finally {
        setIsDoneRequest(true);
      }
    } else {
      setData(null);
    }
  };

  return (
    <>
      {!dataFromLocalStorage && (
        <>
          <div className="text-lg font-semibold mb-3 title-syncx">
            {query?.name}
          </div>
          <CardQueryDescription
            title="Informações da consulta"
            infos={["Base Nacional"]}
            price={selectedPrice}
          >
            <form
              className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md w-fit"
              onSubmit={handleSubmit}
            >
              <div className="flex flex-col md:flex-row gap-2 items-center">
                <QueryVehicleInput
                  info={info}
                  setInfo={setInfo}
                  isLoading={isLoading}
                  error={error}
                  type="PLACA"
                />
                <div>
                  {!errorQuery && data && (
                    <Button
                      className="bg-gray-600"
                      onClick={() => reactToPrintFn()}
                    >
                      <div className="flex gap-2 items-center">
                        <FaPrint />
                        <div>Imprimir</div>
                      </div>
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </CardQueryDescription>

          <SliderToPreview />
        </>
      )}

      {errorQuery ? (
        <Alert color="failure" icon={HiInformationCircle}>
          <span className="font-medium">{errorQuery}</span>
        </Alert>
      ) : (
        <>
          <div
            className={`overflow-x-scroll print-content mobile-fit ${
              data || isLoading ? "block" : "hidden"
            }`}
            ref={(el) => {
              componentRef.current = el;
              printRef.current = el;
            }}
          >
            {!isLoading ? (
              <>
                <Card className="p-5 card-pdf relative overflow-hidden">
                  <HeaderQuery
                    title={query?.name}
                    hasHeader={hasHeaderPDF}
                    date={data?.resumoRetorno?.dataConsulta}
                  />

                  <div className="mt-5">
                    <div className="flex flex-col gap-10">
                      {data?.binNacional && (
                        <div className="text-sm flex-1">
                          <div className="title-vehicle mb-3 flex gap-2 items-center">
                            <div>Informações do veículo - Nacional</div>
                            <img
                              className="w-5"
                              src="/assets/base_nacional.png"
                            />
                          </div>
                          {data?.binNacional?.statusRetorno == 1 ? (
                            <div className="p-2 rounded bg-gray-100 grid grid-cols-3 gap-x-3 gap-y-2">
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Marca/Modelo:{" "}
                                </span>
                                {data?.binNacional?.marcaModelo || "Sem info."}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Ano Fabr./Modelo:{" "}
                                </span>
                                {`${data?.binNacional?.anoFabricacao}/${data?.binNacional?.anoModelo}` ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Tempo de circulação:{" "}
                                </span>
                                {`${
                                  moment().format("YYYY") -
                                  +data?.binNacional?.anoFabricacao
                                } anos` || "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Categoria:{" "}
                                </span>
                                {data?.binNacional?.categoriaVeiculo ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Tipo de veículo:{" "}
                                </span>
                                {data?.binNacional?.tipoVeiculo ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">Placa: </span>
                                {document || "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">RENAVAM: </span>
                                {data?.binNacional?.renavam || "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">Chassi: </span>
                                {data?.binNacional?.chassi || "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Situação:{" "}
                                </span>
                                {data?.binNacional?.situacao || "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">Câmbio: </span>
                                {data?.binNacional?.caixaCambio ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Capacidade Carga:{" "}
                                </span>
                                {data?.binNacional?.capacidadeCarga ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Cilindrada:{" "}
                                </span>
                                {data?.binNacional?.cilindrada ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">CMT: </span>
                                {data?.binNacional?.cmt || "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Combustível:{" "}
                                </span>
                                {data?.binNacional?.combustivel ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">Cor: </span>
                                {data?.binNacional?.corVeiculo ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Emplacamento:{" "}
                                </span>
                                {data?.binNacional?.dataEmplacamento ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">Espécie: </span>
                                {data?.binNacional?.especieVeiculo ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Município:{" "}
                                </span>
                                {data?.binNacional?.municipio ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">UF: </span>
                                {data?.binNacional?.uf || "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Nº Carroceria:{" "}
                                </span>
                                {data?.binNacional?.numeroCarroceria ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">Nº DI: </span>
                                {data?.binNacional?.numeroDI || "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Data registro DI:{" "}
                                </span>
                                {data?.binNacional?.dataRegistroDI ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Nº Eixo Auxiliar:{" "}
                                </span>
                                {data?.binNacional?.numeroEixoAuxiliar ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Nº Eixo Traseiro:{" "}
                                </span>
                                {data?.binNacional?.numeroEixoTrazeiro ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Qtd. Eixos:{" "}
                                </span>
                                {data?.binNacional?.numeroEixos ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Nº Motor:{" "}
                                </span>
                                {data?.binNacional?.numeroMotor ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">PBT: </span>
                                {data?.binNacional?.PBT || "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Potência:{" "}
                                </span>
                                {data?.binNacional?.potenciaVeiculo ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Procedência:{" "}
                                </span>
                                {data?.binNacional?.procedencia ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Qtd. Passageiros:{" "}
                                </span>
                                {data?.binNacional?.quantidadePassageiros ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Situação do chassi:{" "}
                                </span>
                                {data?.binNacional?.situacaoChassi ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Tipo de Carroceria:{" "}
                                </span>
                                {data?.binNacional?.tipoCarroceria ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Tipo Doc. Faturado:{" "}
                                </span>
                                {data?.binNacional?.tipoDocumentoFaturado ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Tipo Doc. Importador:{" "}
                                </span>
                                {data?.binNacional?.tipoDocumentoImportador ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Nº Importador:{" "}
                                </span>
                                {data?.binNacional
                                  ?.numeroIdentificacaoImportador ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Tipo Op. Importação:{" "}
                                </span>
                                {data?.binNacional
                                  ?.tipoOperacaoImportacaoVeiculo ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Montagem:{" "}
                                </span>
                                {data?.binNacional?.tipoMontagem ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  Nº Faturado:{" "}
                                </span>
                                {data?.binNacional
                                  ?.numeroIdentificacaoFaturado ||
                                  "Não informado"}
                              </div>
                              <div className="mb-3">
                                <span className="font-semibold">
                                  UF Faturamento:{" "}
                                </span>
                                {data?.binNacional?.ufFaturamento ||
                                  "Não informado"}
                              </div>
                            </div>
                          ) : (
                            <Alert
                              withBorderAccent
                              color="gray"
                              className="mt-4"
                            >
                              Consulta Indisponível no momento
                            </Alert>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <FooterQuery hasHeader={hasHeaderPDF} />
                </Card>
              </>
            ) : (
              <QueryLoading />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default BaseNacionalQuery;
